import { useCallback } from "react";
import SplashScreen from "@/components/splash-screen";
import RestaurantErrorScreen from "@/components/restaurant/RestaurantErrorScreen";
import { usePublicTenant } from "@/context/PublicTenantProvider";

type PublicTenantGateProps = {
  children: React.ReactNode;
};

/**
 * Blocks the shell until public tenant identity is resolved.
 * Alias / custom_hostname wait on bootstrap; failures and non-tenant hosts
 * (localhost/IP without explicit opt-in) show the unavailable/error screen.
 * Opted-in legacy localhost with a valid restaurant id passes through when resolved.
 */
export default function PublicTenantGate({ children }: PublicTenantGateProps) {
  const { status, error, refetch, restaurantId } = usePublicTenant();

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (status === "idle" || status === "loading") {
    return <SplashScreen />;
  }

  if (status === "unavailable" || status === "error" || !restaurantId.trim()) {
    return (
      <RestaurantErrorScreen
        message={
          error?.message ??
          (status === "error"
            ? "Unable to load storefront."
            : "This storefront is not available.")
        }
        onRetry={handleRetry}
      />
    );
  }

  return <>{children}</>;
}
